import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelText as _vModelText, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "tax-rate-manage-page" }
const _hoisted_2 = { class: "tax-rate-manage-card" }
const _hoisted_3 = { class: "tax-rate-manage-toolbar" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["value"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 0,
  class: "tax-rate-manage-note"
}
const _hoisted_8 = {
  key: 1,
  class: "tax-rate-manage-note"
}
const _hoisted_9 = { class: "tax-rate-manage-grid" }
const _hoisted_10 = { class: "tax-rate-manage-field" }
const _hoisted_11 = { class: "tax-rate-manage-field__input" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "tax-rate-manage-field__label" }
const _hoisted_14 = { class: "tax-rate-manage-field__input" }
const _hoisted_15 = ["onUpdate:modelValue", "disabled", "onBlur"]
const _hoisted_16 = { class: "tax-rate-manage-field" }
const _hoisted_17 = { class: "tax-rate-manage-field__input" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "tax-rate-manage-actions" }
const _hoisted_20 = ["disabled"]

import { computed, onMounted, ref } from 'vue';
import { commonWageApi } from '@/services/api';
import { formatWageNumber, parseWageNumber } from '@/composables/wage/useWageCalculator';
import {
  formatHomeWageRatePercent,
  parseHomeWageRatePercentInput,
} from '@/policies/homeWageSettingPolicy';
import type { CommonWageTaxRate } from '@/types/commonWage';

type RateKey = 'rateYeon' | 'rateGun' | 'rateGo' | 'rateJang' | 'rateSil' | 'rateSan';


export default /*@__PURE__*/_defineComponent({
  __name: 'TaxRateManagePage',
  setup(__props) {

const rateFields: { key: RateKey; label: string }[] = [
  { key: 'rateYeon', label: '국민연금' },
  { key: 'rateGun', label: '건강보험' },
  { key: 'rateGo', label: '고용보험' },
  { key: 'rateJang', label: '장기요양' },
  { key: 'rateSil', label: '실업급여' },
  { key: 'rateSan', label: '산재보험' },
];

const thisYear = new Date().getFullYear();
const editYear = ref(thisYear);
const yearItems = computed(() => {
  const years: number[] = [];
  for (let year = thisYear + 1; year >= thisYear - 4; year -= 1) years.push(year);
  return years;
});

const loading = ref(false);
const saving = ref(false);
const missing = ref(false);
const fallback = ref(false);
const sourceYear = ref<string | number | null>(null);
const minHourly = ref('');
const laborCostRate = ref('');
const rates = ref<Record<RateKey, string>>({
  rateYeon: '',
  rateGun: '',
  rateGo: '',
  rateJang: '',
  rateSil: '',
  rateSan: '',
});

function clearForm() {
  minHourly.value = '';
  laborCostRate.value = '';
  rates.value = {
    rateYeon: '',
    rateGun: '',
    rateGo: '',
    rateJang: '',
    rateSil: '',
    rateSan: '',
  };
}

function fill(data: CommonWageTaxRate) {
  minHourly.value = Number(data.minHourlyWage) > 0 ? formatWageNumber(Number(data.minHourlyWage)) : '';
  laborCostRate.value = formatHomeWageRatePercent(data.laborCostRate, {
    scaleFromDecimal: false,
    withSuffix: false,
  });
  const next = { ...rates.value };
  for (const field of rateFields) {
    next[field.key] = formatHomeWageRatePercent(data[field.key], { withSuffix: false });
  }
  rates.value = next;
}

async function load() {
  loading.value = true;
  try {
    const res = await commonWageApi.getTaxRate(editYear.value);
    const body = res.data as { success?: boolean; data?: CommonWageTaxRate; fallback?: boolean; message?: string };
    if (!body?.success || !body.data) {
      missing.value = true;
      fallback.value = false;
      sourceYear.value = null;
      clearForm();
      return;
    }
    missing.value = false;
    fallback.value = !!body.fallback;
    sourceYear.value = body.data.year;
    fill(body.data);
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: { message?: string } } };
    if (err.response?.status === 404) {
      missing.value = true;
      fallback.value = false;
      sourceYear.value = null;
      clearForm();
      return;
    }
    alert(err.response?.data?.message || '요율을 불러오지 못했습니다.');
  } finally {
    loading.value = false;
  }
}

function blurMin() {
  const amount = parseWageNumber(minHourly.value);
  minHourly.value = amount > 0 ? formatWageNumber(amount) : '';
}

function blurRate(key: RateKey) {
  const parsed = parseHomeWageRatePercentInput(rates.value[key]);
  rates.value = {
    ...rates.value,
    [key]: parsed != null ? formatHomeWageRatePercent(parsed, { withSuffix: false }) : '',
  };
}

function blurLabor() {
  const trimmed = laborCostRate.value.trim().replace(/%/g, '');
  if (!trimmed) {
    laborCostRate.value = '';
    return;
  }
  const amount = parseFloat(trimmed);
  laborCostRate.value = Number.isFinite(amount) ? String(amount) : '';
}

async function save() {
  if (saving.value || loading.value) return;

  const min = parseWageNumber(minHourly.value);
  if (min <= 0) {
    alert('최저시급을 확인해 주세요.');
    return;
  }

  const parsedRates: Record<RateKey, number> = {
    rateYeon: 0,
    rateGun: 0,
    rateGo: 0,
    rateJang: 0,
    rateSil: 0,
    rateSan: 0,
  };
  for (const field of rateFields) {
    const parsed = parseHomeWageRatePercentInput(rates.value[field.key]);
    if (parsed == null) {
      alert(`${field.label} 요율을 확인해 주세요.`);
      return;
    }
    parsedRates[field.key] = parsed;
  }

  const laborTrimmed = laborCostRate.value.trim().replace(/%/g, '');
  const labor = laborTrimmed ? parseFloat(laborTrimmed) : NaN;
  if (!Number.isFinite(labor) || labor < 0) {
    alert('인건비율을 확인해 주세요.');
    return;
  }

  saving.value = true;
  try {
    const res = await commonWageApi.saveTaxRate(editYear.value, {
      minHourlyWage: min,
      ...parsedRates,
      laborCostRate: labor,
    });
    const body = res.data as { success?: boolean; message?: string };
    if (!body?.success) {
      alert(body?.message || '저장에 실패했습니다.');
      return;
    }
    await load();
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
  } finally {
    saving.value = false;
  }
}

onMounted(load);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[9] || (_cache[9] = _createStaticVNode("<header class=\"console-page-head\"><div class=\"console-page-copy\"><p class=\"console-kicker\">설정</p><h2 class=\"console-title\">요율·최저시급</h2><p class=\"console-desc\">연도별 최저시급과 4대보험·인건비 요율입니다. 임금과 예산 계산에 이 값이 쓰입니다.</p></div></header>", 1)),
    _createElementVNode("section", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[3] || (_cache[3] = _createElementVNode("label", {
          class: "tax-rate-manage-label",
          for: "tax-rate-year"
        }, "편집 연도", -1)),
        _withDirectives(_createElementVNode("select", {
          id: "tax-rate-year",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editYear).value = $event)),
          class: "tax-rate-manage-select",
          disabled: loading.value || saving.value,
          onChange: load
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(yearItems.value, (year) => {
            return (_openBlock(), _createElementBlock("option", {
              key: year,
              value: year
            }, _toDisplayString(year) + "년", 9, _hoisted_5))
          }), 128))
        ], 40, _hoisted_4), [
          [
            _vModelSelect,
            editYear.value,
            void 0,
            { number: true }
          ]
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "tax-rate-manage-btn",
          disabled: loading.value || saving.value,
          onClick: load
        }, " 새로고침 ", 8, _hoisted_6)
      ]),
      (missing.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(editYear.value) + "년 자료가 없습니다. 입력 후 저장하면 새로 만듭니다. ", 1))
        : (fallback.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(sourceYear.value) + "년 값이 표시 중입니다. 저장하면 " + _toDisplayString(editYear.value) + "년 자료가 생깁니다. ", 1))
          : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("label", _hoisted_10, [
          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "tax-rate-manage-field__label" }, "최저시급", -1)),
          _createElementVNode("span", _hoisted_11, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((minHourly).value = $event)),
              type: "text",
              inputmode: "numeric",
              disabled: loading.value || saving.value,
              onBlur: blurMin
            }, null, 40, _hoisted_12), [
              [_vModelText, minHourly.value]
            ]),
            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "tax-rate-manage-field__unit" }, "원", -1))
          ])
        ]),
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(rateFields, (field) => {
          return _createElementVNode("label", {
            key: field.key,
            class: "tax-rate-manage-field"
          }, [
            _createElementVNode("span", _hoisted_13, _toDisplayString(field.label), 1),
            _createElementVNode("span", _hoisted_14, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": ($event: any) => ((rates.value[field.key]) = $event),
                type: "text",
                inputmode: "decimal",
                disabled: loading.value || saving.value,
                onBlur: ($event: any) => (blurRate(field.key))
              }, null, 40, _hoisted_15), [
                [_vModelText, rates.value[field.key]]
              ]),
              _cache[6] || (_cache[6] = _createElementVNode("span", { class: "tax-rate-manage-field__unit" }, "%", -1))
            ])
          ])
        }), 64)),
        _createElementVNode("label", _hoisted_16, [
          _cache[8] || (_cache[8] = _createElementVNode("span", { class: "tax-rate-manage-field__label" }, "인건비율", -1)),
          _createElementVNode("span", _hoisted_17, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((laborCostRate).value = $event)),
              type: "text",
              inputmode: "decimal",
              disabled: loading.value || saving.value,
              onBlur: blurLabor
            }, null, 40, _hoisted_18), [
              [_vModelText, laborCostRate.value]
            ]),
            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "tax-rate-manage-field__unit" }, "%", -1))
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createElementVNode("button", {
          type: "button",
          class: "tax-rate-manage-btn tax-rate-manage-btn--save",
          disabled: loading.value || saving.value,
          onClick: save
        }, _toDisplayString(saving.value ? '저장 중' : '저장'), 9, _hoisted_20)
      ])
    ])
  ]))
}
}

})